import type { EvalRow } from './shared';

export type MultiViewAnomaly = {
  role: string;
  evaluator: string;
  avg: number;
  delta: number;
  dir: 'high' | 'low';
  label: string;
  note: string;
};

export type MultiViewProfile = {
  /** 全部参评行（含自评 / 直属 / 下属 / 同级），每行附偏差与状态 */
  rows: Array<{
    role: string;
    evaluator: string;
    avg: number;
    delta: number;
    status: 'high' | 'low' | 'consistent';
  }>;
  anomalies: MultiViewAnomaly[];
  /** 评估投入度提示：哪些评价人 10 维同分（一刀切） */
  flatRows: Array<{ role: string; evaluator: string; sameScore: number; dimCount: number }>;
  consensusTag: '评价高度一致' | '评价存在分歧' | '评价显著分歧';
  consensusTone: 'good' | 'mid' | 'bad';
  consensusNote: string;
  /** 整体卡片色调：good/绿 mid/橙 bad/红 */
  toneClass: 'good' | 'warn' | 'bad';
  /**
   * 一句话画像的结构化叙事，分三段呈现，避免把多名评价人 + 综合判断 + 定档锚点
   * 糊成一大段难以扫读的长句：
   *   - fragments：每个评价人（或同方向群像）的速写句，列表渲染
   *   - kicker：综合判断（不含「综合判断：」前缀，由渲染层加 label）
   *   - anchor：定档锚点说明（不含「定档锚点」前缀，由渲染层加 label）
   */
  narrative: {
    fragments: string[];
    kicker: string;
    anchor: string | null;
  };
  /** 摘要供调试 */
  meta: {
    rowCount: number;
    dimCount: number;
    range: number;
    fullScore: number;
    globalAvg: number;
  };
};

const ANOMALY_DELTA = 1.0;

function anomalyForRole(
  roleLabel: string,
  dir: 'high' | 'low',
): { label: string; note: string } {
  const isHigh = dir === 'high';
  if (roleLabel === '自评') {
    return isHigh
      ? {
          label: '自评偏乐观（自我高估倾向）',
          note: '本人自评显著高于其他评价人，存在自我认知盲区。建议在 1on1 中以具体事实证据反馈，引入多源观察纠偏。',
        }
      : {
          label: '自评偏保守（自我严苛倾向）',
          note: '本人自评显著低于其他评价人，对自身要求较高或低估贡献。建议肯定具体高分行为，引导客观自评，避免错失成长信号。',
        };
  }
  if (roleLabel === '直属领导') {
    return isHigh
      ? {
          label: '直属评价偏宽松',
          note: '直属领导给分明显高于其他视角，可能因熟悉度高 / 偏爱保护带来的高估，需结合同级、跨部门口径再做综合判断。',
        }
      : {
          label: '直属评价偏严苛',
          note: '直属领导给分明显低于其他视角，可能反映标准较高或对本人有特定期望落差，建议在述职复盘中明确具体改进项。',
        };
  }
  if (roleLabel === '下属' || roleLabel.includes('下属')) {
    return isHigh
      ? {
          label: '下属高度认可',
          note: '下属给分显著高于其他视角，团队感受正向；但需注意管理者-下属关系下的"认可偏差"，建议结合同级口径校准。',
        }
      : {
          label: '下属反馈偏低',
          note: '下属给分显著低于其他视角，提示团队管理或协作体验存在问题，建议通过员工访谈定位具体原因。',
        };
  }
  return isHigh
    ? {
        label: `${roleLabel || '同级'}评价偏高`,
        note: '该视角给分明显高于其他视角，横向认可度强；可作为对外口碑的正向信号。',
      }
    : {
        label: `${roleLabel || '同级'}评价偏低`,
        note: '该视角给分明显低于其他视角，提示协作可见度或对外形象有待加强，建议在跨部门关键项目里增加显性贡献。',
      };
}

function isPeerRole(roleLabel: string): boolean {
  return (
    roleLabel === '本部门同事' ||
    roleLabel === '跨部门同事' ||
    roleLabel === '同级' ||
    roleLabel.includes('同级') ||
    roleLabel.includes('部门同事')
  );
}

/**
 * 评价人速写档位（按"是否值得展开人物速写"排序）：
 * - extreme-low / extreme-high：均分压到下/上限附近（如 0～7 标尺均分 ≤2 或 ≥6），态度极端
 * - polarized：维度内极差 ≥ 4，态度撕裂（如 [2,1,1,7,7]）
 * - strong / mid / light：vs 他评的偏移幅度（异常/明显/略偏）
 * - flat：偏移很小（< 0.2）；若同时 isUniform=true，仍单独成段（"一刀切"型）
 */
type VibeBucket =
  | 'extreme-low'
  | 'extreme-high'
  | 'polarized'
  | 'strong'
  | 'mid'
  | 'light'
  | 'flat';
type VibeDir = 'high' | 'low' | 'flat';
type VibeRow = {
  role: string;
  evaluator: string;
  avg: number;
  delta: number;
  bucket: VibeBucket;
  dir: VibeDir;
  /** 该评价人在各维度上的原始分数 */
  dimScores: number[];
  /** 同一名评价人在不同维度上的最低 / 最高 / 极差 */
  spanMin: number;
  spanMax: number;
  spanRange: number;
  /** 全部维度同一分（一刀切） */
  isUniform: boolean;
};

/** 同一名评价人在不同维度上的极差 ≥ 此值 → 撕裂型 */
const POLARIZED_RANGE = 4;
/** 评价人均分 ≤ 此值 → 极端低分（0～7 标尺贴到下限） */
const EXTREME_LOW_AVG = 2;
/** 评价人均分 ≥ 此值 → 极端高分（0～7 标尺贴到上限） */
const EXTREME_HIGH_AVG = 6;

function deltaBucket(delta: number): 'strong' | 'mid' | 'light' | 'flat' {
  const a = Math.abs(delta);
  if (a >= 1.0) return 'strong';
  if (a >= 0.5) return 'mid';
  if (a >= 0.2) return 'light';
  return 'flat';
}

/**
 * 综合 bucket：先看绝对态度（极端 / 撕裂），再看相对偏移（strong/mid/light/flat）。
 * 这样像 [2,1,1,7,7] 这种 |delta| 很小但维度内极差爆表的评价人不会被漏掉。
 */
function bucketOf(avg: number, delta: number, spanRange: number): VibeBucket {
  if (avg <= EXTREME_LOW_AVG) return 'extreme-low';
  if (avg >= EXTREME_HIGH_AVG) return 'extreme-high';
  if (spanRange >= POLARIZED_RANGE) return 'polarized';
  return deltaBucket(delta);
}

function dirOf(delta: number): VibeDir {
  if (delta >= 0.2) return 'high';
  if (delta <= -0.2) return 'low';
  return 'flat';
}

/**
 * 评价人「人物速写」：从角色 × 偏移幅度 × 方向出发，读出**评价人这个人**
 * 在打分行为里透出的性格 / 状态 / 关系暗线（不是再复述一遍数字）。
 * 让 HR 和领导一眼看见"这个人为什么这么打"，而不是"差几分"。
 */
function evaluatorVibeSentence(row: VibeRow): string {
  const who = `「${row.evaluator}」`;
  const role = row.role;
  const { dir, bucket } = row;

  // 1) 极端打分：均分逼近上下限，已经不是"偏一点"，是态度本身极端
  if (bucket === 'extreme-low') {
    if (role === '自评') {
      return `${who}自评几乎把每一项都压到下限（均 ${row.avg.toFixed(1)} / 7），强烈的自我否定型——多见于"对自己要求过高、长期不自信、近期严重挫败感或低自我效能"，建议先用他评里具体高分行为反向校准，避免长期低自评反噬动力`;
    }
    return `${role}${who}几乎把每一项都压到下限（均 ${row.avg.toFixed(1)} / 7），是这一轮里态度最强烈的"否定者"——常见于积压负面印象、利益冲突或刻板信号叠加，不宜单凭此视角定档，应回归具体事件证据`;
  }
  if (bucket === 'extreme-high') {
    if (role === '自评') {
      return `${who}自评几乎把每一项都给到上限（均 ${row.avg.toFixed(1)} / 7），强烈的自我肯定——多见于近期高光兑现期或对外部反馈感知不足，需用 360° 中位数对齐，警惕"自我封神"导致的成长盲区`;
    }
    if (role === '直属领导') {
      return `直属领导${who}几乎把每一项都给到上限（均 ${row.avg.toFixed(1)} / 7），是非常强烈的"力挺型直属"——可能存在隐性保护或带教情绪放大了评分，建议用同级或跨部门口径做一次交叉核验`;
    }
    if (role === '下属' || role === 'SUBORDINATE' || role.includes('下属')) {
      return `下属${who}几乎把每一项都给到上限（均 ${row.avg.toFixed(1)} / 7），强烈正向认可——团队感受很好，但要警惕"上行关系认可偏移"，结合同级口径校准后再使用`;
    }
    return `${role}${who}几乎把每一项都给到上限（均 ${row.avg.toFixed(1)} / 7），是这一轮最强烈的"力挺者"——常因关系亲近、近期共事顺利或带教情绪所致，宜与横向口径交叉核验，避免"光环效应"`;
  }

  // 2) 撕裂：维度内极差极大，价值观/喜好挂在评分上
  if (bucket === 'polarized') {
    if (role === '自评') {
      return `${who}自评在不同维度间高度撕裂（${row.spanMin}～${row.spanMax}，极差 ${row.spanRange}），对自己的"擅长 vs 不擅长"边界极清晰——是非常自我反思型的人，但要注意短板项的自我打分别太极端到错过自我建设机会`;
    }
    if (role === '直属领导') {
      return `直属领导${who}打分在不同维度间高度撕裂（${row.spanMin}～${row.spanMax}，极差 ${row.spanRange}），对某些维度全力肯定、对另一些维度直接打到底——背后多半挂着直属个人的"好恶口径"或具体未闭环事件，需要逐维追问背后是事实差异还是个人偏好`;
    }
    return `${role}${who}打分在不同维度间高度撕裂（${row.spanMin}～${row.spanMax}，极差 ${row.spanRange}），态度分明——某些维度强力肯定、另一些维度强力否定，建议逐维拆解此评价背后是事实差异还是个人偏好`;
  }

  // 3) 一刀切：5/8/10 维同分（信号强度低）
  if (row.isUniform) {
    return `${role}${who}${row.dimScores.length} 项维度全部给同一分（${row.dimScores[0]} 分），未做差异化判断——常见于"接触面有限 / 对评分尺度不熟悉 / 风险规避表态"，信号强度低，加权时建议下调权重或回访补充质性证据`;
  }

  if (role === '自评') {
    if (dir === 'flat') return `${who}自评与他评几乎贴齐，自我画像清晰、不卑不亢，是难得的"自我认知准"`;
    if (dir === 'low') {
      if (bucket === 'strong')
        return `${who}自评显著低于他评，典型的"对自己要求高、不太自信"——背后是严苛的内在标尺，但也意味着可能正在低估自己的真实贡献`;
      if (bucket === 'mid')
        return `${who}自评明显偏保守，谦逊内敛、习惯往内打分，常因为"觉得还不够"而错过对自己亮点的肯定`;
      return `${who}自评略往内收，整体偏审慎，是稳重型的自我评价`;
    }
    if (bucket === 'strong')
      return `${who}自评显著高于他评，自我感觉良好，存在自我认知盲区——对外部期待感知滞后，需要事实证据"接住"自我画像`;
    if (bucket === 'mid')
      return `${who}自评明显偏乐观，自我标尺偏松，对自己下限信心足、上限略微想当然`;
    return `${who}自评略带正向自评，整体自信但仍在合理区间`;
  }

  if (role === '直属领导') {
    if (dir === 'flat') return `直属领导${who}与群体共识对齐，判断稳健，没有明显个人偏移`;
    if (dir === 'low') {
      if (bucket === 'strong')
        return `直属领导${who}整体显著偏严，背后多半藏着具体落差或关键期待尚未对齐——这种偏严往往不是"针对人"，而是有未说破的标准`;
      if (bucket === 'mid')
        return `直属领导${who}明显偏严，标准偏高或对其有更高期待，1on1 中应把优先级落到可观察行为，避免对方"摸不清要的是什么"`;
      return `直属领导${who}略带保留，整体认可但仍留出打磨空间`;
    }
    if (bucket === 'strong')
      return `直属领导${who}显著偏宽松，有可能因熟悉度高 / 隐性保护带来"近距离滤镜"，需要同级或跨部门口径做一次交叉核验`;
    if (bucket === 'mid')
      return `直属领导${who}整体偏宽，对其稳定看好；同时要警惕是否被日常协作的好感放大了评分`;
    return `直属领导${who}略偏正向，是稳定的看好型评价`;
  }

  if (role === '下属' || role === 'SUBORDINATE' || role.includes('下属')) {
    if (dir === 'flat') return `下属${who}反馈与群体一致，团队管理体感平稳，没有暗涌`;
    if (dir === 'low') {
      if (bucket === 'strong')
        return `下属${who}显著偏低，团队管理体感存在张力——这是最容易被忽视的信号，宜用安全通道收集情境事实而非定论`;
      if (bucket === 'mid')
        return `下属${who}明显偏低，管理风格或协作上让人有距离感，是值得在团队访谈里追问的方向`;
      return `下属${who}略偏保留，基本认可但仍有微弱不适感`;
    }
    if (bucket === 'strong')
      return `下属${who}显著偏高，团队高度认可，但需留意上行关系常见的"认可偏移"，单独使用时要结合同级口径校准`;
    if (bucket === 'mid')
      return `下属${who}偏正向，团队感受良好，是难得的人和信号`;
    return `下属${who}略偏正向，团队基本满意`;
  }

  if (
    role === '同级管理者' ||
    role === '斜线/带教' ||
    role.includes('斜线') ||
    role.includes('带教')
  ) {
    if (dir === 'flat') return `${role}${who}与群体共识对齐，跨条线判断平稳`;
    if (dir === 'low') {
      if (bucket === 'strong')
        return `${role}${who}显著偏低，跨条线看到了直属未必看到的风险点或未闭环项，这通常是最值得追问的视角`;
      if (bucket === 'mid')
        return `${role}${who}明显偏审慎，跨条线尚未完全 buy-in——很可能是价值露出 / 事实对齐还没到位，而不是能力本身`;
      return `${role}${who}略偏保留，整体认可但保有疑虑`;
    }
    if (bucket === 'strong')
      return `${role}${who}显著偏正向，跨条线对其外显贡献印象深刻，是典型的"出圈口碑"`;
    if (bucket === 'mid')
      return `${role}${who}偏正向，跨条线积极看好`;
    return `${role}${who}略偏正向，跨条线基本认可`;
  }

  if (isPeerRole(role)) {
    const peer = role || '横向口径';
    if (dir === 'flat') return `${peer}${who}反馈与群体一致，横向口径平稳`;
    if (dir === 'low') {
      if (bucket === 'strong')
        return `${peer}${who}显著偏低，横向协作能见度不足，边界承诺与跟进节奏需要更显性的信号`;
      if (bucket === 'mid')
        return `${peer}${who}偏保守，横向口径上仍有保留，可能是合作触点不够多`;
      return `${peer}${who}略偏保守，基本认可但仍有微小落差`;
    }
    if (bucket === 'strong')
      return `${peer}${who}显著偏高，横向口碑很好，可作为对外正向佐证`;
    if (bucket === 'mid')
      return `${peer}${who}偏认可，横向反馈正向`;
    return `${peer}${who}略偏正向，横向基本认可`;
  }

  if (dir === 'flat') return `${role}${who}与群体共识对齐`;
  return `${role}${who}${dir === 'high' ? '整体偏高' : '整体偏低'}；请结合接触面与样本量理解差异`;
}

type NarrativeBaseline = { role: string; evaluator: string; avg: number };

/**
 * 把全部评价人编织成一段「以评价人视角」的连贯叙事：
 *   1) 自评先讲（最具人物色彩，呼应"自评低 → 不自信 / 对自己要求高"这种判断）
 *   2) 非自评按方向分组：偏正向群（看好）/ 偏审慎群（保留）/ 中枢群（无立场）
 *      —— 同方向且 |delta| 接近的会合并成一句"上下沟通线一致"型的群像
 *      —— 否则各自一句速写，绝不丢人
 *   3) 末尾加一句"综合判断"——HR 听完知道下一步该做什么
 *   4) 最后给定档锚点
 */
function buildEvaluatorNarrative(
  vibeRows: VibeRow[],
  baseline: NarrativeBaseline | null,
): { fragments: string[]; kicker: string; anchor: string | null } {
  if (!vibeRows.length) return { fragments: [], kicker: '', anchor: null };

  const selfRow = vibeRows.find((v) => v.role === '自评');
  const nonSelf = vibeRows.filter((v) => v.role !== '自评');
  const positives = nonSelf
    .filter((v) => v.dir === 'high')
    .sort((a, b) => b.delta - a.delta);
  const negatives = nonSelf
    .filter((v) => v.dir === 'low')
    .sort((a, b) => a.delta - b.delta);
  const flats = nonSelf.filter((v) => v.dir === 'flat');

  const fmtTag = (v: VibeRow): string => `${v.role}「${v.evaluator}」`;
  const tightlyAligned = (vs: VibeRow[]): boolean => {
    if (vs.length < 2) return false;
    const ds = vs.map((v) => Math.abs(v.delta));
    return Math.max(...ds) - Math.min(...ds) <= 0.15;
  };

  const fragments: string[] = [];

  if (selfRow) {
    fragments.push(evaluatorVibeSentence(selfRow));
  }

  if (positives.length >= 2 && tightlyAligned(positives)) {
    const names = positives.map(fmtTag).join('、');
    const allMidOrStronger = positives.every(
      (p) => p.bucket === 'strong' || p.bucket === 'mid',
    );
    fragments.push(
      `${names}口径几乎完全一致、均偏正向${allMidOrStronger ? '——是难得的"正向共识"信号，外部对其稳定看好' : '，整体形成正向看好基调'}`,
    );
  } else {
    for (const p of positives) {
      fragments.push(evaluatorVibeSentence(p));
    }
  }

  if (negatives.length >= 2 && tightlyAligned(negatives)) {
    const names = negatives.map(fmtTag).join('、');
    const allMidOrStronger = negatives.every(
      (n) => n.bucket === 'strong' || n.bucket === 'mid',
    );
    fragments.push(
      `${names}同时偏审慎、幅度相近${allMidOrStronger ? '，是这张画像里需要追问的共同"下拉力"——往往挂着同一类未闭环事件或共同顾虑' : '，整体偏保守'}`,
    );
  } else {
    for (const n of negatives) {
      fragments.push(evaluatorVibeSentence(n));
    }
  }

  const uniformFlats = flats.filter((f) => f.isUniform);
  for (const u of uniformFlats) {
    fragments.push(evaluatorVibeSentence(u));
  }
  const calmFlats = flats.filter((f) => !f.isUniform);
  if (calmFlats.length === 1) {
    fragments.push(`${fmtTag(calmFlats[0])}打分贴齐群体中枢，没有明显个人立场`);
  } else if (calmFlats.length > 1) {
    fragments.push(
      `${calmFlats.map(fmtTag).join('、')}打分均贴齐群体中枢，没有明显个体偏移`,
    );
  }

  const selfDir = selfRow?.dir;
  const selfActive = selfRow != null && selfRow.bucket !== 'flat';
  const noisyCount = vibeRows.filter(
    (v) =>
      v.isUniform ||
      v.bucket === 'extreme-high' ||
      v.bucket === 'extreme-low' ||
      v.bucket === 'polarized',
  ).length;
  const noisyRatio = vibeRows.length > 0 ? noisyCount / vibeRows.length : 0;
  /** "重量级"偏向：strong / mid 才计入 kicker 决策；light 视为"基本一致"不算分歧 */
  const heavyPositives = positives.filter(
    (p) => p.bucket === 'strong' || p.bucket === 'mid',
  );
  const heavyNegatives = negatives.filter(
    (n) => n.bucket === 'strong' || n.bucket === 'mid',
  );

  let kicker: string;
  if (noisyCount >= 3 || noisyRatio >= 0.6) {
    kicker =
      '综合判断：整组评分信号噪声偏大（一刀切 / 极端 / 撕裂占比偏高），现有 360° 数据不足以单独定档；建议先做一次评分尺度校准培训、再用复盘后的二轮数据 + 关键事件证据加权';
  } else if (
    selfActive &&
    selfDir === 'low' &&
    heavyPositives.length >= 1 &&
    heavyNegatives.length === 0
  ) {
    kicker =
      '综合判断：自评是这张画像唯一的"下拉力"，存在明显自我低估；1on1 中应重点用具体高分行为，引导本人接住外部的正向信号';
  } else if (
    selfActive &&
    selfDir === 'high' &&
    heavyNegatives.length >= 1 &&
    heavyPositives.length === 0
  ) {
    kicker =
      '综合判断：自评显著高于多源他评，存在认知盲区；需以可观察事实校正自我画像，避免成长信号被屏蔽';
  } else if (selfActive && selfDir === 'low' && heavyNegatives.length >= 1) {
    kicker =
      '综合判断：自评与部分他评同向偏低，需要分辨是真实存在落差，还是"内敛标尺"被外部部分继承——以具体行为事实而非氛围打分定档';
  } else if (selfActive && selfDir === 'high' && heavyPositives.length >= 1) {
    kicker =
      '综合判断：自评与部分他评同向偏高，需要分辨是真实兑现，还是"亲近圈层"光环效应——以横向 / 跨条线视角交叉核验';
  } else if (heavyPositives.length === 0 && heavyNegatives.length === 0 && !selfActive) {
    kicker = '综合判断：多源高度收敛，画像稳定，可直接采用群体均值定档';
  } else if (heavyPositives.length >= 1 && heavyNegatives.length >= 1) {
    kicker =
      '综合判断：各视角立场分歧明显，定档不宜依赖单一视角；建议回到事实证据 / 关键事件复盘后再做加权';
  } else if (heavyPositives.length >= 1) {
    kicker = '综合判断：他评整体偏正向，定档可在群体均值基础上略向上靠';
  } else if (heavyNegatives.length >= 1) {
    kicker = '综合判断：他评整体偏审慎，定档不宜冒进，需把保留意见一项项问清楚';
  } else {
    kicker = '综合判断：以群体均值定档为主，关注后续是否有新的事实证据';
  }

  const kickerBody = kicker.replace(/^综合判断：/, '');
  const anchor = baseline
    ? `参考${baseline.role}「${baseline.evaluator}」（均分 ${baseline.avg.toFixed(2)} 分），最接近群体中枢。`
    : null;

  return { fragments, kicker: kickerBody, anchor };
}

export function buildMultiViewProfile(rows: EvalRow[] | undefined): MultiViewProfile | null {
  if (!rows?.length) return null;
  const dimCount = rows[0]?.dimensions?.length ?? 0;
  if (dimCount <= 0 || rows.length < 2) return null;

  const totalSum = rows.reduce((a, b) => a + b.rowTotal, 0);
  const globalAvg = totalSum / (rows.length * dimCount);
  const totals = rows.map((r) => r.rowTotal);
  const range = totals.length ? Math.max(...totals) - Math.min(...totals) : 0;
  const fullScore = dimCount * 7;

  const anomalies: MultiViewAnomaly[] = [];
  const tableRows: MultiViewProfile['rows'] = [];
  for (const r of rows) {
    const others = rows.filter((x) => x !== r);
    if (!others.length) continue;
    const meanOthers =
      others.reduce((a, b) => a + b.rowTotal, 0) / (others.length * dimCount);
    const thisAvg = r.rowTotal / dimCount;
    const delta = thisAvg - meanOthers;
    let status: 'high' | 'low' | 'consistent';
    if (delta >= ANOMALY_DELTA) status = 'high';
    else if (delta <= -ANOMALY_DELTA) status = 'low';
    else status = 'consistent';

    tableRows.push({ role: r.roleLabel, evaluator: r.evaluatorName, avg: thisAvg, delta, status });

    if (status !== 'consistent') {
      const dir: 'high' | 'low' = status;
      const meta = anomalyForRole(r.roleLabel, dir);
      anomalies.push({
        role: r.roleLabel,
        evaluator: r.evaluatorName,
        avg: thisAvg,
        delta,
        dir,
        label: meta.label,
        note: meta.note,
      });
    }
  }

  let consensusTag: MultiViewProfile['consensusTag'];
  let consensusTone: MultiViewProfile['consensusTone'];
  let consensusNote: string;
  if (range < 5) {
    consensusTag = '评价高度一致';
    consensusTone = 'good';
    consensusNote = '不同视角给分高度收敛，结论稳健可信，可作为高置信度参照。';
  } else if (range <= 15) {
    consensusTag = '评价存在分歧';
    consensusTone = 'mid';
    consensusNote = '不同视角给分存在中度差异，建议结合下方异常视角逐项追问后再做判断。';
  } else {
    consensusTag = '评价显著分歧';
    consensusTone = 'bad';
    consensusNote = '不同视角给分严重不一致，单一视角不可作为定档依据，需以共识口径或事实证据为准。';
  }

  const flatRows: MultiViewProfile['flatRows'] = [];
  for (const r of rows) {
    const ss = r.dimensions.map((d) => d.score);
    if (ss.length > 0 && Math.max(...ss) === Math.min(...ss)) {
      flatRows.push({
        role: r.roleLabel,
        evaluator: r.evaluatorName,
        sameScore: ss[0],
        dimCount: ss.length,
      });
    }
  }

  // 一句话画像：以「评价人 × 性格 / 状态」为主线，把每位评价人的打分行为读成人物速写；
  // 数字细节留在表格里，这里只讲"为什么这么打"。
  const peerCandidates = tableRows.filter((r) => isPeerRole(r.role));
  const baselineCandidates = peerCandidates.length ? peerCandidates : tableRows;
  const baseline = baselineCandidates.reduce(
    (a, b) => (Math.abs(a.delta) <= Math.abs(b.delta) ? a : b),
    baselineCandidates[0],
  );

  const vibeRows: VibeRow[] = tableRows.map((r, idx) => {
    const dimScores = rows[idx]?.dimensions.map((d) => d.score) ?? [];
    const spanMin = dimScores.length ? Math.min(...dimScores) : 0;
    const spanMax = dimScores.length ? Math.max(...dimScores) : 0;
    const spanRange = spanMax - spanMin;
    const isUniform = dimScores.length > 0 && spanRange === 0;
    return {
      role: r.role,
      evaluator: r.evaluator,
      avg: r.avg,
      delta: r.delta,
      bucket: bucketOf(r.avg, r.delta, spanRange),
      dir: dirOf(r.delta),
      dimScores,
      spanMin,
      spanMax,
      spanRange,
      isUniform,
    };
  });

  const narrative = buildEvaluatorNarrative(
    vibeRows,
    baseline
      ? { role: baseline.role, evaluator: baseline.evaluator, avg: baseline.avg }
      : null,
  );

  let toneClass: MultiViewProfile['toneClass'];
  if (consensusTone === 'bad' || anomalies.length >= 2) toneClass = 'bad';
  else if (consensusTone === 'mid' || anomalies.length === 1) toneClass = 'warn';
  else toneClass = 'good';

  return {
    rows: tableRows,
    anomalies,
    flatRows,
    consensusTag,
    consensusTone,
    consensusNote,
    toneClass,
    narrative,
    meta: { rowCount: rows.length, dimCount, range, fullScore, globalAvg },
  };
}
